import { useState, useEffect, useCallback } from 'react';
import {
  Megaphone, Plus, Pencil, Trash2, MapPin, Calendar, Users,
  DollarSign, Image, ExternalLink, Eye, EyeOff, Heart, Download,
  ClipboardList, MessageSquare, Phone, ChevronDown, Youtube, Check, Loader2,
  Mail, Instagram, Sparkles, Music,
} from 'lucide-react';
import { Modal, Toggle, Spinner } from '@/components/shared/UIComponents';
import {
  fetchBulletins, createBulletin, updateBulletin, deleteBulletin,
  fetchBulletinInterests,
  fetchAllBulletinInterests, updateBulletinInterest,
  fetchBookingInquiries, updateBookingInquiryStatus,
  fetchYouTubeVideos,
} from '@/lib/api';
import type {
  Tenant, Bulletin, CreateBulletinInput, UpdateBulletinInput,
  BulletinInterest, BulletinInterestStatus,
  BookingInquiry, BookingInquiryStatus,
  YouTubeVideoPreview,
} from '@/types';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatPrice(amount: number | null, currency: string): string {
  if (amount === null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(amount);
}

/** Extract a YouTube video ID from any common YouTube URL format */
function getYouTubeId(url: string): string | null {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return match ? match[1] : null;
}

const EMPTY_FORM = {
  title: '',
  description: '',
  destinations: '',
  start_date: '',
  end_date: '',
  price_from: '',
  price_currency: 'USD',
  group_size_max: '',
  image_url: '',
  video_url: '',
  booking_url: '',
  is_active: true,
};

export function BulletinsPage({ tenant }: { tenant: Tenant }) {
  const [bulletins, setBulletins] = useState<Bulletin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Bulletin | null>(null);
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<Bulletin | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Interest viewer
  const [interestTarget, setInterestTarget] = useState<Bulletin | null>(null);
  const [interests, setInterests] = useState<BulletinInterest[]>([]);
  const [loadingInterests, setLoadingInterests] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<'listings' | 'inquiries' | 'interested'>('listings');

  // Inquiry state
  const [inquiries, setInquiries] = useState<BookingInquiry[]>([]);
  const [inquiriesLoading, setInquiriesLoading] = useState(false);
  const [inquiryFilter, setInquiryFilter] = useState<BookingInquiryStatus | 'all'>('all');
  const [updatingInquiryId, setUpdatingInquiryId] = useState<string | null>(null);
  const [editingNotesId, setEditingNotesId] = useState<string | null>(null);
  const [editingNotesValue, setEditingNotesValue] = useState('');

  // Interested-tab state — every user who tapped the heart on any of this
  // tenant's bulletins, with light CRM (status, notes, mailto follow-up).
  const [allInterests, setAllInterests] = useState<BulletinInterest[]>([]);
  const [allInterestsLoading, setAllInterestsLoading] = useState(false);
  const [interestFilter, setInterestFilter] = useState<BulletinInterestStatus | 'all'>('all');
  const [updatingInterestId, setUpdatingInterestId] = useState<string | null>(null);
  const [editingInterestNotesId, setEditingInterestNotesId] = useState<string | null>(null);
  const [editingInterestNotesValue, setEditingInterestNotesValue] = useState('');

  // Instagram → AI draft state
  const [showIgModal, setShowIgModal] = useState(false);
  const [igPostUrl, setIgPostUrl] = useState('');
  const [igCaption, setIgCaption] = useState('');
  const [igImageUrl, setIgImageUrl] = useState('');
  const [igDrafting, setIgDrafting] = useState(false);
  const [igError, setIgError] = useState('');

  // TikTok → AI draft state (mirror of IG flow)
  const [showTtModal, setShowTtModal] = useState(false);
  const [ttPostUrl, setTtPostUrl] = useState('');
  const [ttCaption, setTtCaption] = useState('');
  const [ttImageUrl, setTtImageUrl] = useState('');
  const [ttDrafting, setTtDrafting] = useState(false);
  // ttFetching = pulling caption from oEmbed (separate from ttDrafting
  // which is the Claude draft step). Two distinct spinners so the
  // operator knows which step is in flight.
  const [ttFetching, setTtFetching] = useState(false);
  const [ttError, setTtError] = useState('');

  // YouTube import state
  const [showYouTubeModal, setShowYouTubeModal] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [ytFetching, setYtFetching] = useState(false);
  const [ytVideos, setYtVideos] = useState<YouTubeVideoPreview[]>([]);
  const [ytSelected, setYtSelected] = useState<Set<string>>(new Set());
  const [ytRemainingQuota, setYtRemainingQuota] = useState<number>(0);
  const [ytMaxAllowed, setYtMaxAllowed] = useState<number>(20);
  const [ytImporting, setYtImporting] = useState(false);
  const [ytImportProgress, setYtImportProgress] = useState(0);
  const [ytError, setYtError] = useState('');

  // Form state
  const [form, setForm] = useState(EMPTY_FORM);

  // ── Starter content seed ──────────────────────────────────────────────
  // For new tenants (especially travel influencers) the empty state isn't
  // useful. One click to drop 3 generic-but-good travel listings so the
  // splash page demo isn't blank, and so the admin sees what fields look
  // like populated. Edit / delete freely afterwards.
  const [seeding, setSeeding] = useState(false);
  const [seedError, setSeedError] = useState<string | null>(null);

  const handleSeedStarter = async () => {
    setSeeding(true);
    setSeedError(null);
    try {
      const STARTER: CreateBulletinInput[] = [
        {
          title:        'Tuscany Foodie Trail · 7 days',
          description:  'A curated week through Florence, Siena, and Chianti hill towns — wineries, family-run trattorias, and a Tuscan cooking class. Built for travellers who plan their day around dinner.',
          destinations: ['Florence', 'Siena', 'Chianti'],
          price_from:   2400, price_currency: 'USD',
          group_size_max: 8,
          image_url:    'https://images.unsplash.com/photo-1534445867742-43195f401b6c?w=1200',
          is_active:    true,
        },
        {
          title:        'Tokyo + Kyoto Cherry Blossoms · 10 days',
          description:  'Late-March itinerary timed to peak sakura. Quiet shrines at sunrise, Tsukiji breakfast, an overnight ryokan stay, and a slow afternoon under Maruyama Park lanterns.',
          destinations: ['Tokyo', 'Kyoto'],
          price_from:   3800, price_currency: 'USD',
          group_size_max: 12,
          image_url:    'https://images.unsplash.com/photo-1492571350019-22de08371fd3?w=1200',
          is_active:    true,
        },
        {
          title:        'Bali Yoga & Reset · 5 days',
          description:  'Ubud-based recharge with a daily sunrise vinyasa class, jungle cafes, a temple-water purification, and one optional surf morning in Canggu.',
          destinations: ['Ubud', 'Canggu'],
          price_from:   1450, price_currency: 'USD',
          group_size_max: 10,
          image_url:    'https://images.unsplash.com/photo-1518002171953-a080ee817e1f?w=1200',
          is_active:    true,
        },
      ];
      for (const b of STARTER) {
        await createBulletin(tenant.id, b);
      }
      await load();
    } catch (e) {
      setSeedError(e instanceof Error ? e.message : 'Could not add starter listings');
    } finally {
      setSeeding(false);
    }
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchBulletins(tenant.id);
      setBulletins(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load bulletins');
    } finally {
      setLoading(false);
    }
  }, [tenant.id]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (b: Bulletin) => {
    setEditing(b);
    setForm({
      title: b.title,
      description: b.description,
      destinations: b.destinations.join(', '),
      start_date: b.start_date || '',
      end_date: b.end_date || '',
      price_from: b.price_from !== null ? String(b.price_from) : '',
      price_currency: b.price_currency,
      group_size_max: b.group_size_max !== null ? String(b.group_size_max) : '',
      image_url: b.image_url || '',
      video_url: b.video_url || '',
      booking_url: b.booking_url || '',
      is_active: b.is_active,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.description.trim()) return;
    setSaving(true);
    try {
      const destinations = form.destinations
        .split(',')
        .map(d => d.trim())
        .filter(Boolean);

      const payload: CreateBulletinInput | UpdateBulletinInput = {
        title: form.title.trim(),
        description: form.description.trim(),
        destinations,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        price_from: form.price_from ? parseFloat(form.price_from) : null,
        price_currency: form.price_currency || 'USD',
        group_size_max: form.group_size_max ? parseInt(form.group_size_max) : null,
        image_url: form.image_url.trim() || null,
        video_url: form.video_url.trim() || null,
        booking_url: form.booking_url.trim() || null,
        is_active: form.is_active,
      };

      if (editing) {
        await updateBulletin(editing.id, tenant.id, payload);
      } else {
        await createBulletin(tenant.id, payload as CreateBulletinInput);
      }
      setShowModal(false);
      await load();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteBulletin(deleteTarget.id, tenant.id);
      setBulletins(prev => prev.filter(b => b.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (b: Bulletin) => {
    try {
      const updated = await updateBulletin(b.id, tenant.id, { is_active: !b.is_active });
      setBulletins(prev => prev.map(x => x.id === b.id ? updated : x));
    } catch (err: any) {
      setError(err.message || 'Failed to toggle');
    }
  };

  const openInterests = async (b: Bulletin) => {
    setInterestTarget(b);
    setLoadingInterests(true);
    try {
      const data = await fetchBulletinInterests(b.id);
      setInterests(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load interests');
    } finally {
      setLoadingInterests(false);
    }
  };

  const downloadInterests = async (b: Bulletin) => {
    try {
      const data = await fetchBulletinInterests(b.id);
      if (data.length === 0) {
        setError('No interested users to download for this listing.');
        return;
      }

      // CSV with BOM for proper Excel UTF-8 support
      const BOM = '\uFEFF';
      const headers = ['Tour Title', 'Start Date', 'End Date', 'Full Name', 'Email', 'Date Interested'];
      const rows = data.map(i => [
        b.title,
        b.start_date ? formatDate(b.start_date) : '',
        b.end_date ? formatDate(b.end_date) : '',
        i.user_name || '',
        i.user_email || '',
        formatDate(i.created_at),
      ]);

      const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
      const csv = BOM + [headers, ...rows].map(row => row.map(escape).join(',')).join('\r\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${b.title.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_')}_interests.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to download interests');
    }
  };

  // ── Inquiry functions ──────────────────────────────────────────
  const loadInquiries = useCallback(async () => {
    setInquiriesLoading(true);
    try {
      const statusFilter = inquiryFilter === 'all' ? undefined : inquiryFilter;
      const data = await fetchBookingInquiries(tenant.id, statusFilter);
      setInquiries(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load inquiries');
    } finally {
      setInquiriesLoading(false);
    }
  }, [tenant.id, inquiryFilter]);

  useEffect(() => {
    if (activeTab === 'inquiries') loadInquiries();
  }, [activeTab, loadInquiries]);

  // ── Interested-tab loaders + mutators ──────────────────────────
  const loadAllInterests = useCallback(async () => {
    setAllInterestsLoading(true);
    try {
      const data = await fetchAllBulletinInterests(tenant.id, interestFilter);
      setAllInterests(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load interested users');
    } finally {
      setAllInterestsLoading(false);
    }
  }, [tenant.id, interestFilter]);

  useEffect(() => {
    if (activeTab === 'interested') loadAllInterests();
  }, [activeTab, loadAllInterests]);

  const handleInterestStatusChange = async (
    interest: BulletinInterest,
    newStatus: BulletinInterestStatus,
  ) => {
    setUpdatingInterestId(interest.id);
    try {
      const updated = await updateBulletinInterest(interest.id, { status: newStatus });
      setAllInterests(prev =>
        prev.map(i =>
          i.id === interest.id
            ? { ...updated, bulletin_title: i.bulletin_title }
            : i,
        ),
      );
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
    } finally {
      setUpdatingInterestId(null);
    }
  };

  const saveInterestNotes = async (interest: BulletinInterest) => {
    setUpdatingInterestId(interest.id);
    try {
      const updated = await updateBulletinInterest(interest.id, {
        admin_notes: editingInterestNotesValue || null,
      });
      setAllInterests(prev =>
        prev.map(i =>
          i.id === interest.id
            ? { ...updated, bulletin_title: i.bulletin_title }
            : i,
        ),
      );
      setEditingInterestNotesId(null);
    } catch (err: any) {
      setError(err.message || 'Failed to save notes');
    } finally {
      setUpdatingInterestId(null);
    }
  };

  // ── Instagram → AI bulletin draft ──────────────────────────────
  // Tenant pastes a caption (and optionally an IG URL + image URL); the
  // ai-bulletin-from-ig edge function calls Claude to extract structured
  // fields, then we open the bulletin form pre-filled with the draft so
  // the admin can review and save in one click.
  const handleAiDraftFromIg = async () => {
    if (!igCaption.trim() || igCaption.trim().length < 10) {
      setIgError('Paste an Instagram caption (at least 10 characters).');
      return;
    }
    setIgDrafting(true);
    setIgError('');
    try {
      const platformUrl = import.meta.env.VITE_SUPABASE_URL || '';
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      const res = await fetch(`${platformUrl}/functions/v1/ai-bulletin-from-ig`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
          'apikey': anonKey,
        },
        body: JSON.stringify({
          caption: igCaption.trim(),
          image_url: igImageUrl.trim() || undefined,
          post_url: igPostUrl.trim() || undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        setIgError(json.error || 'Could not draft the bulletin from this caption.');
        return;
      }

      // Pre-fill the bulletin form with the AI's draft and open it.
      const d = json.draft;
      setEditing(null);
      setForm({
        title: d.title || '',
        description: d.description || '',
        destinations: (d.destinations || []).join(', '),
        start_date: d.suggested_start_date || '',
        end_date: d.suggested_end_date || '',
        price_from: d.price_from != null ? String(d.price_from) : '',
        price_currency: d.price_currency || 'USD',
        group_size_max: d.group_size_max != null ? String(d.group_size_max) : '',
        image_url: d.image_url || '',
        video_url: '',
        booking_url: '',
        is_active: false, // open as draft so admin reviews before going live
      });
      setShowIgModal(false);
      setIgPostUrl('');
      setIgCaption('');
      setIgImageUrl('');
      setShowModal(true);
    } catch (err: any) {
      setIgError(err?.message || 'Something went wrong. Try again.');
    } finally {
      setIgDrafting(false);
    }
  };

  // ── TikTok → oEmbed caption fetch (URL → caption + thumbnail) ───
  // Lets the operator click "Fetch from URL" and have the caption +
  // cover image pre-filled from TikTok's public oEmbed endpoint. They
  // can then review / edit before triggering the Claude draft step.
  // One-step alternative: just hit "Draft Bulletin" with a URL but no
  // caption — the edge fn auto-fetches and runs Claude in one call.
  const handleFetchFromTikTokUrl = async () => {
    const url = ttPostUrl.trim();
    if (!url) {
      setTtError('Paste a TikTok URL first.');
      return;
    }
    setTtFetching(true);
    setTtError('');
    try {
      const platformUrl = import.meta.env.VITE_SUPABASE_URL || '';
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      const res = await fetch(`${platformUrl}/functions/v1/ai-bulletin-from-tiktok`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
          'apikey': anonKey,
        },
        body: JSON.stringify({
          mode:     'fetch_only',
          post_url: url,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        setTtError(json.error || 'Could not fetch this TikTok URL.');
        return;
      }
      setTtCaption(json.caption || '');
      if (json.image_url) setTtImageUrl(json.image_url);
    } catch (err: any) {
      setTtError(err?.message || 'Network error fetching from TikTok.');
    } finally {
      setTtFetching(false);
    }
  };

  // ── TikTok → AI bulletin draft (mirrors IG flow) ────────────────
  const handleAiDraftFromTikTok = async () => {
    if (!ttCaption.trim() || ttCaption.trim().length < 5) {
      setTtError('Paste a TikTok caption (at least 5 characters).');
      return;
    }
    setTtDrafting(true);
    setTtError('');
    try {
      const platformUrl = import.meta.env.VITE_SUPABASE_URL || '';
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      const res = await fetch(`${platformUrl}/functions/v1/ai-bulletin-from-tiktok`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
          'apikey': anonKey,
        },
        body: JSON.stringify({
          caption: ttCaption.trim(),
          image_url: ttImageUrl.trim() || undefined,
          post_url: ttPostUrl.trim() || undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        setTtError(json.error || 'Could not draft the bulletin from this caption.');
        return;
      }

      const d = json.draft;
      setEditing(null);
      setForm({
        title: d.title || '',
        description: d.description || '',
        destinations: (d.destinations || []).join(', '),
        start_date: d.suggested_start_date || '',
        end_date: d.suggested_end_date || '',
        price_from: d.price_from != null ? String(d.price_from) : '',
        price_currency: d.price_currency || 'USD',
        group_size_max: d.group_size_max != null ? String(d.group_size_max) : '',
        image_url: d.image_url || '',
        // The TikTok URL goes into the bulletin's video_url so the splash
        // card can show the play indicator — you can clear it from the
        // form before saving if you don't want a video on the listing.
        video_url: ttPostUrl.trim() || '',
        booking_url: '',
        is_active: false,
      });
      setShowTtModal(false);
      setTtPostUrl('');
      setTtCaption('');
      setTtImageUrl('');
      setShowModal(true);
    } catch (err: any) {
      setTtError(err?.message || 'Something went wrong. Try again.');
    } finally {
      setTtDrafting(false);
    }
  };

  const downloadAllInterests = () => {
    if (allInterests.length === 0) {
      setError('No interested users to download.');
      return;
    }
    const BOM = '﻿';
    const headers = ['Listing', 'Status', 'Full Name', 'Email', 'Admin Notes', 'Date Interested', 'Date Contacted'];
    const rows = allInterests.map(i => [
      i.bulletin_title || '',
      i.status,
      i.user_name || '',
      i.user_email || '',
      i.admin_notes || '',
      formatDate(i.created_at),
      i.contacted_at ? formatDate(i.contacted_at) : '',
    ]);
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const csv = BOM + [headers, ...rows].map(row => row.map(escape).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `interested_users_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /**
   * Build a mailto: link with a sensible prefilled subject + body so the
   * admin can reach out to the interested user in one click. We escape the
   * inputs because email + name come from B2C user input and the bulletin
   * title is admin-controlled but still user-visible elsewhere.
   */
  const buildFollowUpMailto = (interest: BulletinInterest): string | null => {
    if (!interest.user_email) return null;
    const firstName = (interest.user_name || '').split(/\s+/)[0] || 'there';
    const tour = interest.bulletin_title || 'our trip';
    const subject = `Following up on ${tour}`;
    const body =
      `Hi ${firstName},\n\n` +
      `Thanks for showing interest in "${tour}". I'd love to chat and answer any questions you have about it.\n\n` +
      `When works for a quick call, or do you prefer to keep things over email?\n\n` +
      `— ${tenant.name || 'The team'}`;
    return `mailto:${interest.user_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const newInterestCount = allInterests.filter(i => i.status === 'new').length;

  const handleInquiryStatusChange = async (inquiry: BookingInquiry, newStatus: BookingInquiryStatus) => {
    setUpdatingInquiryId(inquiry.id);
    try {
      const updated = await updateBookingInquiryStatus(inquiry.id, tenant.id, newStatus, inquiry.admin_notes || undefined);
      setInquiries(prev => prev.map(i => i.id === inquiry.id ? updated : i));
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
    } finally {
      setUpdatingInquiryId(null);
    }
  };

  const saveAdminNotes = async (inquiry: BookingInquiry) => {
    setUpdatingInquiryId(inquiry.id);
    try {
      const updated = await updateBookingInquiryStatus(inquiry.id, tenant.id, inquiry.status, editingNotesValue);
      setInquiries(prev => prev.map(i => i.id === inquiry.id ? updated : i));
      setEditingNotesId(null);
    } catch (err: any) {
      setError(err.message || 'Failed to save notes');
    } finally {
      setUpdatingInquiryId(null);
    }
  };

  const downloadInquiries = () => {
    if (inquiries.length === 0) {
      setError('No inquiries to download.');
      return;
    }
    const BOM = '\uFEFF';
    const headers = ['Listing', 'Status', 'Full Name', 'Email', 'Phone', 'Message', 'Start Date', 'End Date', 'Group Size', 'Admin Notes', 'Date Submitted'];
    const rows = inquiries.map(i => [
      i.bulletin_title || '',
      i.status,
      i.user_name || '',
      i.user_email || '',
      i.user_phone || '',
      i.message || '',
      i.preferred_start_date || '',
      i.preferred_end_date || '',
      i.group_size ? String(i.group_size) : '',
      i.admin_notes || '',
      formatDate(i.created_at),
    ]);
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const csv = BOM + [headers, ...rows].map(row => row.map(escape).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `booking_inquiries_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const newInquiryCount = inquiries.filter(i => i.status === 'new').length;

  // ── YouTube import functions ──────────────────────────────────────
  const handleFetchYouTube = async () => {
    if (!youtubeUrl.trim()) return;
    setYtFetching(true);
    setYtError('');
    setYtVideos([]);
    setYtSelected(new Set());
    try {
      const result = await fetchYouTubeVideos(youtubeUrl.trim(), tenant.id);
      setYtVideos(result.videos);
      setYtRemainingQuota(result.remaining_quota);
      setYtMaxAllowed(result.max_allowed);
      // Auto-select all videos
      setYtSelected(new Set(result.videos.map(v => v.video_id)));
    } catch (err: any) {
      setYtError(err.message || 'Failed to fetch videos');
    } finally {
      setYtFetching(false);
    }
  };

  const toggleYtVideo = (videoId: string) => {
    setYtSelected(prev => {
      const next = new Set(prev);
      if (next.has(videoId)) {
        next.delete(videoId);
      } else if (next.size < ytRemainingQuota) {
        next.add(videoId);
      }
      return next;
    });
  };

  const handleYouTubeImport = async () => {
    if (ytSelected.size === 0) return;
    setYtImporting(true);
    setYtImportProgress(0);

    const selectedVideos = ytVideos.filter(v => ytSelected.has(v.video_id));
    let imported = 0;

    // Get existing video URLs to skip duplicates
    const existingUrls = new Set(
      bulletins.filter(b => b.video_url).map(b => b.video_url!)
    );

    for (const video of selectedVideos) {
      const videoUrl = `https://www.youtube.com/watch?v=${video.video_id}`;
      if (existingUrls.has(videoUrl)) {
        imported++;
        setYtImportProgress(Math.round((imported / selectedVideos.length) * 100));
        continue; // Skip duplicates
      }
      try {
        await createBulletin(tenant.id, {
          title: video.title,
          description: video.description.slice(0, 1000),
          destinations: [],
          start_date: null,
          end_date: null,
          price_from: null,
          price_currency: 'USD',
          group_size_max: null,
          image_url: video.thumbnail_url || null,
          video_url: videoUrl,
          booking_url: null,
          is_active: false, // Import as draft
        });
      } catch (err) {
        console.error(`[YouTube Import] Failed to import "${video.title}":`, err);
      }
      imported++;
      setYtImportProgress(Math.round((imported / selectedVideos.length) * 100));
    }

    setYtImporting(false);
    setShowYouTubeModal(false);
    setYoutubeUrl('');
    setYtVideos([]);
    setYtSelected(new Set());
    await load(); // Refresh listings
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  const activeCount = bulletins.filter(b => b.is_active).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-surface-900">Bulletin Board</h1>
          <p className="text-sm text-surface-500">
            Post tour packages and travel deals for your customers
          </p>
          <p className="text-xs text-surface-400 mt-1">
            These listings and videos will appear on your app's Home page
          </p>
        </div>
        {activeTab === 'listings' && (
          <div className="flex items-center gap-2">
            {/* Import buttons — ghost style with brand-color icon accent.
                Cohesive design language across all three import sources;
                each one only telegraphs its provenance through the
                colored icon, not a saturated gradient block. */}
            <button
              onClick={() => setShowIgModal(true)}
              className="group flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium border border-surface-300/80 bg-surface-0 text-surface-700 hover:bg-pink-50/60 hover:border-pink-300/70 hover:text-pink-700 dark:bg-surface-100 dark:border-surface-200 dark:text-surface-700 dark:hover:bg-pink-950/30 dark:hover:border-pink-800/50 dark:hover:text-pink-300 transition-colors shadow-sm hover:shadow"
              title="Paste an Instagram caption — Claude drafts the bulletin for you"
            >
              <Instagram size={15} className="text-pink-600 dark:text-pink-400 group-hover:scale-110 transition-transform" />
              Instagram
            </button>
            <button
              onClick={() => setShowTtModal(true)}
              className="group flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium border border-surface-300/80 bg-surface-0 text-surface-700 hover:bg-cyan-50/60 hover:border-cyan-300/70 hover:text-cyan-700 dark:bg-surface-100 dark:border-surface-200 dark:text-surface-700 dark:hover:bg-cyan-950/30 dark:hover:border-cyan-800/50 dark:hover:text-cyan-300 transition-colors shadow-sm hover:shadow"
              title="Paste a TikTok caption — Claude drafts the bulletin for you"
            >
              <Music size={15} className="text-cyan-600 dark:text-cyan-400 group-hover:scale-110 transition-transform" />
              TikTok
            </button>
            <button
              onClick={() => setShowYouTubeModal(true)}
              className="group flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium border border-surface-300/80 bg-surface-0 text-surface-700 hover:bg-red-50/60 hover:border-red-300/70 hover:text-red-700 dark:bg-surface-100 dark:border-surface-200 dark:text-surface-700 dark:hover:bg-red-950/30 dark:hover:border-red-800/50 dark:hover:text-red-300 transition-colors shadow-sm hover:shadow"
              title="Pull videos from a YouTube channel"
            >
              <Youtube size={15} className="text-red-600 dark:text-red-400 group-hover:scale-110 transition-transform" />
              YouTube
            </button>
            <div className="w-px h-6 bg-surface-300/60 mx-1" aria-hidden />
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-700 transition-colors shadow-sm hover:shadow"
            >
              <Plus size={16} /> New Listing
            </button>
          </div>
        )}
        {activeTab === 'inquiries' && inquiries.length > 0 && (
          <button
            onClick={downloadInquiries}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-700 transition-colors"
          >
            <Download size={16} /> Export CSV
          </button>
        )}
        {activeTab === 'interested' && allInterests.length > 0 && (
          <button
            onClick={downloadAllInterests}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-700 transition-colors"
          >
            <Download size={16} /> Export CSV
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-surface-100 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab('listings')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'listings'
              ? 'bg-surface-0 text-surface-900 shadow-sm border border-surface-300/60'
              : 'text-surface-500 hover:text-surface-700'
          }`}
        >
          <Megaphone size={16} /> Listings
        </button>
        <button
          onClick={() => setActiveTab('inquiries')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'inquiries'
              ? 'bg-surface-0 text-surface-900 shadow-sm border border-surface-300/60'
              : 'text-surface-500 hover:text-surface-700'
          }`}
        >
          <ClipboardList size={16} /> Inquiries
          {newInquiryCount > 0 && (
            <span className="px-1.5 py-0.5 bg-amber-500 text-white text-xs font-bold rounded-full min-w-[20px] text-center">
              {newInquiryCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('interested')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'interested'
              ? 'bg-surface-0 text-surface-900 shadow-sm border border-surface-300/60'
              : 'text-surface-500 hover:text-surface-700'
          }`}
        >
          <Heart size={16} /> Interested
          {newInterestCount > 0 && (
            <span className="px-1.5 py-0.5 bg-rose-500 text-white text-xs font-bold rounded-full min-w-[20px] text-center">
              {newInterestCount}
            </span>
          )}
        </button>
      </div>

      {error && (
        <div className="px-4 py-3 banner-red border rounded-xl text-sm text-red-400">
          {error}
          <button onClick={() => setError('')} className="ml-2 font-medium underline">Dismiss</button>
        </div>
      )}

      {/* ══════════════════════ LISTINGS TAB ══════════════════════ */}
      {activeTab === 'listings' && (
        <>
          {/* Active indicator — soft, professional, brand-leaning emerald */}
          {activeCount > 0 && (
            <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl border border-emerald-200/80 bg-emerald-50/70 dark:bg-emerald-950/40 dark:border-emerald-800/40">
              <div className="w-2 h-2 rounded-full bg-emerald-600 dark:bg-emerald-400 ring-4 ring-emerald-600/15 dark:ring-emerald-400/20 animate-pulse" />
              <span className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
                {activeCount} listing{activeCount !== 1 ? 's' : ''} live on the app
              </span>
            </div>
          )}

      {/* Bulletins list */}
      {bulletins.length === 0 ? (
        <div className="text-center py-16">
          <Megaphone className="w-12 h-12 text-surface-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-surface-700 mb-1">No listings yet</h3>
          <p className="text-sm text-surface-500 mb-6">
            Create your first tour listing to show on your customers' app, or drop 3 starter listings to see how the page comes together.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={openCreate}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-700 transition-colors"
            >
              <Plus size={16} /> Create your first listing
            </button>
            <button
              onClick={handleSeedStarter}
              disabled={seeding}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-surface-100 hover:bg-surface-200 text-surface-700 rounded-xl text-sm font-medium transition-colors disabled:opacity-60"
            >
              {seeding ? <Loader2 size={16} className="animate-spin" /> : <Megaphone size={16} />}
              {seeding ? 'Adding…' : 'Add 3 starter listings'}
            </button>
          </div>
          {seedError && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-3">{seedError}</p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {bulletins.map(b => (
            <div
              key={b.id}
              className={`border rounded-xl p-5 transition-all ${
                b.is_active
                  // Active card: soft emerald tint in light mode (clearly
                  // "this is live" without being neon), deeper in dark mode.
                  ? 'border-emerald-200/80 bg-emerald-50/40 shadow-sm hover:shadow-md hover:border-emerald-300/80 dark:border-emerald-800/40 dark:bg-emerald-900/10'
                  // Draft card: a touch darker than pure white for light
                  // mode so the cards lift off the page; muted opacity in
                  // dark mode just like before.
                  : 'border-surface-300/70 bg-surface-100/60 hover:bg-surface-100 hover:border-surface-400/70 dark:border-surface-200 dark:bg-surface-0 dark:opacity-60 dark:hover:border-surface-300'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  {/* Status badges */}
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    {b.is_active && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700/50">
                        <Eye size={12} /> Live
                      </span>
                    )}
                    {!b.is_active && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-surface-200/70 text-surface-600 border border-surface-300/70 dark:bg-surface-100 dark:text-surface-500 dark:border-surface-200">
                        <EyeOff size={12} /> Draft
                      </span>
                    )}
                    {b.destinations.length > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium badge-blue border border-blue-200 dark:border-blue-700/50">
                        <MapPin size={12} />
                        {b.destinations.slice(0, 2).join(', ')}
                        {b.destinations.length > 2 && ` +${b.destinations.length - 2}`}
                      </span>
                    )}
                  </div>

                  {/* Title */}
                  <h3 className="font-semibold text-surface-900 mb-1">{b.title}</h3>
                  <p className="text-sm text-surface-600 line-clamp-2 mb-2">{b.description}</p>

                  {/* Meta */}
                  <div className="flex items-center gap-4 text-xs text-surface-400 flex-wrap">
                    {b.start_date && (
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {formatDate(b.start_date)}
                        {b.end_date && ` — ${formatDate(b.end_date)}`}
                      </span>
                    )}
                    {b.price_from !== null && (
                      <span className="flex items-center gap-1">
                        <DollarSign size={12} />
                        From {formatPrice(b.price_from, b.price_currency)}
                      </span>
                    )}
                    {b.group_size_max && (
                      <span className="flex items-center gap-1">
                        <Users size={12} />
                        Up to {b.group_size_max}
                      </span>
                    )}
                    {b.image_url && (
                      <span className="flex items-center gap-1">
                        <Image size={12} /> Has image
                      </span>
                    )}
                    {b.video_url && (
                      <span className="flex items-center gap-1 text-red-400">
                        <svg viewBox="0 0 24 24" width={12} height={12} fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                        YouTube
                      </span>
                    )}
                    {b.booking_url && (
                      <span className="flex items-center gap-1">
                        <ExternalLink size={12} /> Has link
                      </span>
                    )}
                    <span>Created {formatDate(b.created_at)}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => openInterests(b)}
                    className="p-2 rounded-lg hover:bg-surface-100 text-surface-500 hover:text-surface-700 transition-colors"
                    title="View interests"
                  >
                    <Heart size={16} />
                  </button>
                  <button
                    onClick={() => downloadInterests(b)}
                    className="p-2 rounded-lg hover:bg-surface-100 text-surface-500 hover:text-surface-700 transition-colors"
                    title="Download interests as Excel"
                  >
                    <Download size={16} />
                  </button>
                  <Toggle
                    enabled={b.is_active}
                    onChange={() => handleToggleActive(b)}
                  />
                  <button
                    onClick={() => openEdit(b)}
                    className="p-2 rounded-lg hover:bg-surface-100 text-surface-500 hover:text-surface-700 transition-colors"
                    title="Edit"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(b)}
                    className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-50 dark:bg-red-900/20 text-surface-400 hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Create / Edit Modal ──────────────────────────────── */}
      {showModal && (
        <Modal
          open={showModal}
          title={editing ? 'Edit Listing' : 'New Tour Listing'}
          onClose={() => setShowModal(false)}
        >
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">Title *</label>
              <input
                type="text"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Bali Cultural Tour — 7 Days"
                maxLength={150}
                className="w-full px-3 py-2 rounded-lg border border-surface-300 bg-surface-50 text-sm text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">Description *</label>
              <textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Describe the tour package, highlights, what's included..."
                rows={4}
                maxLength={1000}
                className="w-full px-3 py-2 rounded-lg border border-surface-300 bg-surface-50 text-sm text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
              />
              <div className="text-xs text-surface-400 text-right mt-1">{form.description.length}/1000</div>
            </div>

            {/* Destinations */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">Destinations</label>
              <input
                type="text"
                value={form.destinations}
                onChange={e => setForm(f => ({ ...f, destinations: e.target.value }))}
                placeholder="e.g. Bali, Ubud, Seminyak (comma separated)"
                className="w-full px-3 py-2 rounded-lg border border-surface-300 bg-surface-50 text-sm text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
              <p className="text-xs text-surface-400 mt-1">Separate multiple destinations with commas</p>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={form.start_date}
                  onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-surface-300 bg-surface-50 text-sm text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={form.end_date}
                  onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-surface-300 bg-surface-50 text-sm text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Price & Group Size */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1">Price From</label>
                <input
                  type="number"
                  value={form.price_from}
                  onChange={e => setForm(f => ({ ...f, price_from: e.target.value }))}
                  placeholder="1299"
                  min="0"
                  step="1"
                  className="w-full px-3 py-2 rounded-lg border border-surface-300 bg-surface-50 text-sm text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1">Currency</label>
                <select
                  value={form.price_currency}
                  onChange={e => setForm(f => ({ ...f, price_currency: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-surface-300 bg-surface-50 text-sm text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="INR">INR</option>
                  <option value="AUD">AUD</option>
                  <option value="CAD">CAD</option>
                  <option value="JPY">JPY</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1">Max Group</label>
                <input
                  type="number"
                  value={form.group_size_max}
                  onChange={e => setForm(f => ({ ...f, group_size_max: e.target.value }))}
                  placeholder="20"
                  min="1"
                  className="w-full px-3 py-2 rounded-lg border border-surface-300 bg-surface-50 text-sm text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Image URL */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">Cover Image URL</label>
              <input
                type="url"
                value={form.image_url}
                onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                placeholder="https://example.com/tour-image.jpg"
                className="w-full px-3 py-2 rounded-lg border border-surface-300 bg-surface-50 text-sm text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
              {form.image_url && (
                <div className="mt-2 rounded-lg overflow-hidden border border-surface-200 h-32">
                  <img src={form.image_url} alt="Preview" className="w-full h-full object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                </div>
              )}
            </div>

            {/* YouTube Video URL */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">YouTube Video URL</label>
              <input
                type="url"
                value={form.video_url}
                onChange={e => setForm(f => ({ ...f, video_url: e.target.value }))}
                placeholder="https://www.youtube.com/watch?v=..."
                className="w-full px-3 py-2 rounded-lg border border-surface-300 bg-surface-50 text-sm text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
              <p className="text-xs text-surface-400 mt-1">
                Paste any YouTube link (watch, shorts, embed). A thumbnail with play button will show on the app.
              </p>
              {form.video_url && getYouTubeId(form.video_url) && (
                <div className="mt-2 rounded-lg overflow-hidden border border-surface-200 h-32 relative">
                  <img
                    src={`https://img.youtube.com/vi/${getYouTubeId(form.video_url)}/hqdefault.jpg`}
                    alt="Video thumbnail"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center shadow-lg">
                      <svg viewBox="0 0 24 24" className="w-6 h-6 text-white ml-0.5" fill="currentColor">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                </div>
              )}
              {form.video_url && !getYouTubeId(form.video_url) && (
                <p className="text-xs text-red-400 mt-1">⚠ Could not detect a YouTube video ID from this URL</p>
              )}
            </div>

            {/* Booking URL */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">Booking / Details URL</label>
              <input
                type="url"
                value={form.booking_url}
                onChange={e => setForm(f => ({ ...f, booking_url: e.target.value }))}
                placeholder="https://yoursite.com/book/bali-tour"
                className="w-full px-3 py-2 rounded-lg border border-surface-300 bg-surface-50 text-sm text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>

            {/* Active toggle */}
            <div className="flex items-center justify-between py-2">
              <div>
                <div className="text-sm font-medium text-surface-700">Publish immediately</div>
                <div className="text-xs text-surface-400">Show this listing on the app right away</div>
              </div>
              <Toggle
                enabled={form.is_active}
                onChange={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2 border-t border-surface-200">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-surface-600 hover:text-surface-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.title.trim() || !form.description.trim()}
                className="px-5 py-2 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Delete Confirmation ──────────────────────────────── */}
      {deleteTarget && (
        <Modal
          open={!!deleteTarget}
          title="Delete Listing"
          onClose={() => setDeleteTarget(null)}
        >
          <div className="space-y-4">
            <p className="text-sm text-surface-600">
              Are you sure you want to delete <strong>"{deleteTarget.title}"</strong>? This will also remove all interest data. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm font-medium text-surface-600 hover:text-surface-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-5 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Interests Viewer ──────────────────────────────────── */}
      {interestTarget && (
        <Modal
          open={!!interestTarget}
          title={`Interested Users — ${interestTarget.title}`}
          onClose={() => { setInterestTarget(null); setInterests([]); }}
        >
          <div className="space-y-3">
            {loadingInterests ? (
              <div className="flex justify-center py-8">
                <Spinner size="md" />
              </div>
            ) : interests.length === 0 ? (
              <div className="text-center py-8">
                <Heart className="w-10 h-10 text-surface-300 mx-auto mb-2" />
                <p className="text-sm text-surface-500">No interests yet</p>
                <p className="text-xs text-surface-400 mt-1">
                  Users will appear here when they tap "I'm Interested" on the app
                </p>
              </div>
            ) : (
              <>
                <div className="text-sm text-surface-500 mb-2">
                  {interests.length} user{interests.length !== 1 ? 's' : ''} interested
                </div>
                <div className="max-h-80 overflow-y-auto space-y-2">
                  {interests.map(i => (
                    <div
                      key={i.id}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-surface-300/70 bg-surface-100/60 dark:border-surface-200 dark:bg-surface-0"
                    >
                      <div className="w-8 h-8 rounded-full bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-500 text-xs font-bold shrink-0">
                        {(i.user_name || i.user_email || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-surface-900 truncate">
                          {i.user_name || 'Anonymous'}
                        </div>
                        {i.user_email && (
                          <div className="text-xs text-surface-500 truncate">{i.user_email}</div>
                        )}
                      </div>
                      <div className="text-xs text-surface-400 shrink-0">
                        {formatDate(i.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
        </>
      )}

      {/* ══════════════════════ INQUIRIES TAB ══════════════════════ */}
      {activeTab === 'inquiries' && (
        <div className="space-y-4">
          {/* Filter row */}
          <div className="flex items-center gap-2">
            {(['all', 'new', 'contacted', 'closed'] as const).map(f => (
              <button
                key={f}
                onClick={() => setInquiryFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  inquiryFilter === f
                    ? 'bg-brand-600 text-white'
                    : 'bg-surface-100 text-surface-500 hover:text-surface-700'
                }`}
              >
                {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          {inquiriesLoading ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : inquiries.length === 0 ? (
            <div className="text-center py-16">
              <ClipboardList className="w-12 h-12 text-surface-300 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-surface-700 mb-1">No inquiries yet</h3>
              <p className="text-sm text-surface-500">
                Booking inquiries from users will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-sm text-surface-500">
                {inquiries.length} inquir{inquiries.length !== 1 ? 'ies' : 'y'}
              </div>
              {inquiries.map(inq => (
                <div
                  key={inq.id}
                  className="border border-surface-300/70 rounded-xl p-5 bg-surface-100/60 dark:border-surface-200 dark:bg-surface-0 space-y-3"
                >
                  {/* Top row: user + status */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-500 text-sm font-bold shrink-0">
                        {(inq.user_name || inq.user_email || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-surface-900">
                          {inq.user_name || 'Anonymous'}
                        </div>
                        {inq.user_email && (
                          <div className="text-xs text-surface-500">{inq.user_email}</div>
                        )}
                        {inq.user_phone && (
                          <div className="text-xs text-surface-500 flex items-center gap-1 mt-0.5">
                            <Phone size={10} /> {inq.user_phone}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status selector */}
                    <div className="flex items-center gap-2 shrink-0">
                      <select
                        value={inq.status}
                        onChange={(e) => handleInquiryStatusChange(inq, e.target.value as BookingInquiryStatus)}
                        disabled={updatingInquiryId === inq.id}
                        className={`px-2.5 py-1 rounded-lg text-xs font-medium border cursor-pointer transition-colors ${
                          inq.status === 'new'
                            ? 'badge-amber border-amber-200 dark:border-amber-700/50'
                            : inq.status === 'contacted'
                            ? 'badge-blue border-blue-200 dark:border-blue-700/50'
                            : 'badge-emerald border-emerald-200 dark:border-emerald-700/50'
                        }`}
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                  </div>

                  {/* Bulletin title */}
                  {inq.bulletin_title && (
                    <div className="text-xs text-surface-400 flex items-center gap-1">
                      <Megaphone size={12} /> {inq.bulletin_title}
                    </div>
                  )}

                  {/* Message */}
                  {inq.message && (
                    <div className="px-3 py-2.5 bg-surface-50 rounded-lg border border-surface-200">
                      <div className="text-xs text-surface-400 mb-1 font-medium">Message</div>
                      <p className="text-sm text-surface-700 whitespace-pre-wrap">{inq.message}</p>
                    </div>
                  )}

                  {/* Details row */}
                  <div className="flex items-center gap-4 text-xs text-surface-400 flex-wrap">
                    {inq.preferred_start_date && (
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {formatDate(inq.preferred_start_date)}
                        {inq.preferred_end_date && ` — ${formatDate(inq.preferred_end_date)}`}
                      </span>
                    )}
                    {inq.group_size && (
                      <span className="flex items-center gap-1">
                        <Users size={12} /> {inq.group_size} travelers
                      </span>
                    )}
                    <span>Submitted {formatDate(inq.created_at)}</span>
                  </div>

                  {/* Admin notes */}
                  <div>
                    {editingNotesId === inq.id ? (
                      <div className="space-y-2">
                        <textarea
                          value={editingNotesValue}
                          onChange={(e) => setEditingNotesValue(e.target.value)}
                          placeholder="Add internal notes..."
                          rows={2}
                          className="w-full px-3 py-2 rounded-lg border border-surface-300 bg-surface-50 text-sm text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => saveAdminNotes(inq)}
                            disabled={updatingInquiryId === inq.id}
                            className="px-3 py-1 bg-brand-600 text-white rounded-lg text-xs font-medium hover:bg-brand-700 disabled:opacity-50 transition-colors"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingNotesId(null)}
                            className="px-3 py-1 text-surface-500 hover:text-surface-700 text-xs font-medium transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingNotesId(inq.id);
                          setEditingNotesValue(inq.admin_notes || '');
                        }}
                        className="text-xs text-surface-400 hover:text-surface-600 transition-colors"
                      >
                        {inq.admin_notes ? (
                          <span className="flex items-center gap-1">
                            <MessageSquare size={12} /> {inq.admin_notes}
                          </span>
                        ) : (
                          '+ Add notes'
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════ INTERESTED TAB ══════════════════════ */}
      {/* Lists every user who tapped the heart on any of this tenant's
          bulletins. Each row is a lightweight CRM card: status pill, a
          one-click email-follow-up button, and inline admin notes. Same
          shape and affordances as the Inquiries tab so the admin's muscle
          memory transfers between the two. */}
      {activeTab === 'interested' && (
        <div className="space-y-4">
          {/* Filter row */}
          <div className="flex items-center gap-2">
            {(['all', 'new', 'contacted', 'archived'] as const).map(f => (
              <button
                key={f}
                onClick={() => setInterestFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  interestFilter === f
                    ? 'bg-brand-600 text-white'
                    : 'bg-surface-100 text-surface-500 hover:text-surface-700'
                }`}
              >
                {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          {allInterestsLoading ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : allInterests.length === 0 ? (
            <div className="text-center py-16">
              <Heart className="w-12 h-12 text-surface-300 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-surface-700 mb-1">
                {interestFilter === 'all' ? 'No interest yet' : `No ${interestFilter} interests`}
              </h3>
              <p className="text-sm text-surface-500">
                When someone taps the heart on one of your listings they'll show up here.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-sm text-surface-500">
                {allInterests.length} interested user{allInterests.length !== 1 ? 's' : ''}
              </div>
              {allInterests.map(interest => {
                const mailto = buildFollowUpMailto(interest);
                return (
                  <div
                    key={interest.id}
                    className="border border-surface-300/70 rounded-xl p-5 bg-surface-100/60 dark:border-surface-200 dark:bg-surface-0 space-y-3"
                  >
                    {/* Top row: user + status */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-500 text-sm font-bold shrink-0">
                          {(interest.user_name || interest.user_email || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-surface-900 truncate">
                            {interest.user_name || 'Anonymous'}
                          </div>
                          {interest.user_email && (
                            <div className="text-xs text-surface-500 truncate">{interest.user_email}</div>
                          )}
                        </div>
                      </div>

                      {/* Follow-up + status */}
                      <div className="flex items-center gap-2 shrink-0">
                        {mailto ? (
                          <a
                            href={mailto}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-600 hover:bg-brand-700 text-white transition-colors"
                            title={`Email ${interest.user_email}`}
                          >
                            <Mail size={12} /> Follow up
                          </a>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface-100 text-surface-400 cursor-not-allowed"
                            title="No email on file"
                          >
                            <Mail size={12} /> No email
                          </span>
                        )}
                        <select
                          value={interest.status}
                          onChange={(e) =>
                            handleInterestStatusChange(interest, e.target.value as BulletinInterestStatus)
                          }
                          disabled={updatingInterestId === interest.id}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium border cursor-pointer transition-colors ${
                            interest.status === 'new'
                              ? 'badge-amber border-amber-200 dark:border-amber-700/50'
                              : interest.status === 'contacted'
                              ? 'badge-blue border-blue-200 dark:border-blue-700/50'
                              : 'badge-emerald border-emerald-200 dark:border-emerald-700/50'
                          }`}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="archived">Archived</option>
                        </select>
                      </div>
                    </div>

                    {/* Bulletin title + dates */}
                    <div className="flex items-center gap-4 text-xs text-surface-400 flex-wrap">
                      {interest.bulletin_title && (
                        <span className="flex items-center gap-1">
                          <Megaphone size={12} /> {interest.bulletin_title}
                        </span>
                      )}
                      <span>Tapped {formatDate(interest.created_at)}</span>
                      {interest.contacted_at && (
                        <span className="flex items-center gap-1 text-blue-400">
                          <Check size={12} /> Contacted {formatDate(interest.contacted_at)}
                        </span>
                      )}
                    </div>

                    {/* Admin notes — same inline-edit pattern as Inquiries */}
                    <div>
                      {editingInterestNotesId === interest.id ? (
                        <div className="space-y-2">
                          <textarea
                            value={editingInterestNotesValue}
                            onChange={(e) => setEditingInterestNotesValue(e.target.value)}
                            placeholder="Add internal notes..."
                            rows={2}
                            className="w-full px-3 py-2 rounded-lg border border-surface-300 bg-surface-50 text-sm text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => saveInterestNotes(interest)}
                              disabled={updatingInterestId === interest.id}
                              className="px-3 py-1 bg-brand-600 text-white rounded-lg text-xs font-medium hover:bg-brand-700 disabled:opacity-50 transition-colors"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingInterestNotesId(null)}
                              className="px-3 py-1 text-surface-500 hover:text-surface-700 text-xs font-medium transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setEditingInterestNotesId(interest.id);
                            setEditingInterestNotesValue(interest.admin_notes || '');
                          }}
                          className="text-xs text-surface-400 hover:text-surface-600 transition-colors"
                        >
                          {interest.admin_notes ? (
                            <span className="flex items-center gap-1">
                              <MessageSquare size={12} /> {interest.admin_notes}
                            </span>
                          ) : (
                            '+ Add notes'
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════ TIKTOK AI DRAFT MODAL ══════════════════════ */}
      {/* Same shape as the Instagram modal — paste TikTok caption (and
          optionally URL + cover image), Claude drafts the bulletin via
          the ai-bulletin-from-tiktok edge function, form pre-fills. */}
      {showTtModal && (
        <Modal
          open={showTtModal}
          title="Draft a Bulletin from TikTok"
          onClose={() => { if (!ttDrafting) { setShowTtModal(false); setTtError(''); } }}
        >
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-gradient-to-br from-cyan-50 via-white to-pink-50 border border-cyan-200/60 dark:from-cyan-950/40 dark:via-zinc-900 dark:to-pink-950/40 dark:border-cyan-700/50">
              <Sparkles size={16} className="text-cyan-600 dark:text-cyan-300 mt-0.5 shrink-0" />
              {/* Dark-mode surface scale is inverted in this codebase:
                  surface-900 is the *light* primary-text color (#f1f5f9),
                  surface-100 is the sidebar bg (near-black). For high
                  contrast in dark mode use the HIGH end of the scale. */}
              <div className="text-xs text-surface-800 dark:text-surface-900 leading-relaxed">
                Paste the caption from one of your TikTok videos. Claude reads it and drafts a bulletin (title, description, destinations) so you don't retype anything. You'll review it before saving.
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">
                TikTok Video URL <span className="text-surface-400 font-normal">(optional)</span>
              </label>
              {/* Two-column row: URL input + Fetch button. Fetch calls the
                  edge fn's fetch_only mode which pulls caption + thumbnail
                  from TikTok's public oEmbed — no auth, no API token. */}
              <div className="flex gap-2">
                <input
                  type="url"
                  value={ttPostUrl}
                  onChange={(e) => setTtPostUrl(e.target.value)}
                  placeholder="https://www.tiktok.com/@user/video/1234567890"
                  disabled={ttDrafting || ttFetching}
                  className="flex-1 px-3 py-2 rounded-lg border border-surface-300 bg-surface-50 text-sm text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={handleFetchFromTikTokUrl}
                  disabled={!ttPostUrl.trim() || ttDrafting || ttFetching}
                  title="Pull caption + thumbnail from this URL via TikTok's public oEmbed"
                  className="px-3 py-2 rounded-lg text-sm font-semibold text-cyan-700 dark:text-cyan-300 bg-cyan-500/15 hover:bg-cyan-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  {ttFetching ? 'Fetching…' : 'Fetch'}
                </button>
              </div>
              <p className="text-xs text-surface-400 mt-1">
                Paste a URL and click <strong>Fetch</strong> to pull caption + cover automatically. Otherwise paste the caption manually below.
                The URL also goes onto the bulletin as its video link, so the splash card shows a play indicator.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">
                Caption <span className="text-red-500">*</span>
              </label>
              <textarea
                value={ttCaption}
                onChange={(e) => setTtCaption(e.target.value)}
                placeholder="Paste the full TikTok caption here — hashtags, emoji, location tags, all of it. Claude cleans it up."
                rows={6}
                disabled={ttDrafting}
                maxLength={2200}
                className="w-full px-3 py-2 rounded-lg border border-surface-300 bg-surface-50 text-sm text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
              />
              <div className="text-xs text-surface-400 text-right mt-1">{ttCaption.length}/2200</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">
                Cover Image URL <span className="text-surface-400 font-normal">(optional)</span>
              </label>
              <input
                type="url"
                value={ttImageUrl}
                onChange={(e) => setTtImageUrl(e.target.value)}
                placeholder="A still frame from the video, or any cover image URL"
                disabled={ttDrafting}
                className="w-full px-3 py-2 rounded-lg border border-surface-300 bg-surface-50 text-sm text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>

            {ttError && (
              <div className="px-3 py-2.5 rounded-lg banner-red border text-sm text-red-400">
                {ttError}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => { setShowTtModal(false); setTtError(''); }}
                disabled={ttDrafting}
                className="flex-1 px-4 py-2.5 rounded-xl border border-surface-300 text-surface-700 text-sm font-medium hover:bg-surface-100 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAiDraftFromTikTok}
                disabled={ttDrafting || !ttCaption.trim()}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold transition-opacity bg-gradient-to-tr from-cyan-400 via-zinc-900 to-pink-500 hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {ttDrafting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {ttDrafting ? 'Drafting…' : 'Draft with AI'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ══════════════════════ INSTAGRAM AI DRAFT MODAL ══════════════════════ */}
      {/* Paste an IG caption (and optionally URL + image) → Claude drafts the
          bulletin and pre-fills the Listing form. Admin reviews and saves.
          Lite version: no IG API integration — caption is the primary signal,
          image URL passes through. */}
      {showIgModal && (
        <Modal
          open={showIgModal}
          title="Draft a Bulletin from Instagram"
          onClose={() => { if (!igDrafting) { setShowIgModal(false); setIgError(''); } }}
        >
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-gradient-to-br from-fuchsia-50 to-amber-50 border border-pink-200/60 dark:from-fuchsia-950/40 dark:to-amber-950/40 dark:border-pink-700/50">
              <Sparkles size={16} className="text-pink-600 dark:text-pink-300 mt-0.5 shrink-0" />
              {/* See TikTok modal note above on inverted dark-mode surface scale. */}
              <div className="text-xs text-surface-800 dark:text-surface-900 leading-relaxed">
                Paste the caption from one of your Instagram posts. Claude reads it and drafts a bulletin (title, description, destinations) so you don't retype anything. You'll review it before saving.
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">
                Instagram Post URL <span className="text-surface-400 font-normal">(optional)</span>
              </label>
              <input
                type="url"
                value={igPostUrl}
                onChange={(e) => setIgPostUrl(e.target.value)}
                placeholder="https://www.instagram.com/p/abcd1234/"
                disabled={igDrafting}
                className="w-full px-3 py-2 rounded-lg border border-surface-300 bg-surface-50 text-sm text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">
                Caption <span className="text-red-500">*</span>
              </label>
              <textarea
                value={igCaption}
                onChange={(e) => setIgCaption(e.target.value)}
                placeholder="Paste the full IG post caption here — hashtags, emoji, location tags, all of it. Claude cleans it up."
                rows={8}
                disabled={igDrafting}
                maxLength={4000}
                className="w-full px-3 py-2 rounded-lg border border-surface-300 bg-surface-50 text-sm text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
              />
              <div className="text-xs text-surface-400 text-right mt-1">{igCaption.length}/4000</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">
                Cover Image URL <span className="text-surface-400 font-normal">(optional)</span>
              </label>
              <input
                type="url"
                value={igImageUrl}
                onChange={(e) => setIgImageUrl(e.target.value)}
                placeholder="Right-click the IG photo → Copy image address, paste here"
                disabled={igDrafting}
                className="w-full px-3 py-2 rounded-lg border border-surface-300 bg-surface-50 text-sm text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
              <p className="text-xs text-surface-400 mt-1">
                Goes straight onto the bulletin's cover. Leave blank and add later in the form.
              </p>
            </div>

            {igError && (
              <div className="px-3 py-2.5 rounded-lg banner-red border text-sm text-red-400">
                {igError}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => { setShowIgModal(false); setIgError(''); }}
                disabled={igDrafting}
                className="flex-1 px-4 py-2.5 rounded-xl border border-surface-300 text-surface-700 text-sm font-medium hover:bg-surface-100 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAiDraftFromIg}
                disabled={igDrafting || !igCaption.trim()}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold transition-opacity bg-gradient-to-tr from-fuchsia-600 via-pink-500 to-amber-400 hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {igDrafting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {igDrafting ? 'Drafting…' : 'Draft with AI'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ══════════════════════ YOUTUBE IMPORT MODAL ══════════════════════ */}
      {showYouTubeModal && (
        <Modal
          open={showYouTubeModal}
          title="Import from YouTube"
          onClose={() => { if (!ytImporting) { setShowYouTubeModal(false); setYtVideos([]); setYtError(''); setYoutubeUrl(''); } }}
        >
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {/* Step 1: URL input */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">YouTube Channel or Playlist URL</label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={youtubeUrl}
                  onChange={e => setYoutubeUrl(e.target.value)}
                  placeholder="https://youtube.com/@channel or playlist URL"
                  disabled={ytFetching || ytImporting}
                  className="flex-1 px-3 py-2 rounded-lg border border-surface-300 bg-surface-50 text-sm text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
                <button
                  onClick={handleFetchYouTube}
                  disabled={ytFetching || ytImporting || !youtubeUrl.trim()}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors shrink-0"
                >
                  {ytFetching ? <Loader2 size={14} className="animate-spin" /> : <Youtube size={14} />}
                  Fetch
                </button>
              </div>
              <p className="text-xs text-surface-400 mt-1">
                Supports: youtube.com/@handle, youtube.com/channel/..., youtube.com/playlist?list=...
              </p>
            </div>

            {ytError && (
              <div className="px-4 py-3 banner-red border rounded-xl text-sm text-red-400">
                {ytError}
              </div>
            )}

            {/* Quota badge */}
            {ytVideos.length > 0 && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-surface-500">
                  {ytVideos.length} video{ytVideos.length !== 1 ? 's' : ''} found · {ytSelected.size} selected
                </span>
                <span className="px-2 py-1 badge-blue border border-blue-800/40 rounded-lg font-medium">
                  Quota: {ytRemainingQuota} of {ytMaxAllowed} remaining
                </span>
              </div>
            )}

            {/* Step 2: Video selection */}
            {ytVideos.length > 0 && (
              <div className="space-y-2">
                {/* Select all / none */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setYtSelected(new Set(ytVideos.slice(0, ytRemainingQuota).map(v => v.video_id)))}
                    className="text-xs text-brand-500 hover:underline"
                  >
                    Select all
                  </button>
                  <span className="text-surface-300">·</span>
                  <button
                    onClick={() => setYtSelected(new Set())}
                    className="text-xs text-surface-400 hover:underline"
                  >
                    Clear
                  </button>
                </div>

                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {ytVideos.map(video => {
                    const isSelected = ytSelected.has(video.video_id);
                    const existing = bulletins.some(b => b.video_url === `https://www.youtube.com/watch?v=${video.video_id}`);
                    return (
                      <div
                        key={video.video_id}
                        onClick={() => !existing && !ytImporting && toggleYtVideo(video.video_id)}
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          existing
                            ? 'border-surface-200 bg-surface-50 opacity-50 cursor-default'
                            : isSelected
                            ? 'border-brand-500/40 bg-brand-500/5'
                            : 'border-surface-200 bg-surface-0 hover:border-surface-300'
                        }`}
                      >
                        {/* Checkbox */}
                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                          existing
                            ? 'border-surface-300 bg-surface-200'
                            : isSelected
                            ? 'border-brand-500 bg-brand-500'
                            : 'border-surface-300'
                        }`}>
                          {(isSelected || existing) && <Check size={12} className={existing ? 'text-surface-400' : 'text-white'} />}
                        </div>

                        {/* Thumbnail */}
                        <div className="w-20 h-12 rounded-md overflow-hidden shrink-0 relative">
                          <img
                            src={video.thumbnail_url}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-surface-900 line-clamp-1">{video.title}</div>
                          <div className="text-xs text-surface-500 mt-0.5">
                            {video.channel_title}
                            {video.published_at && ` · ${formatDate(video.published_at)}`}
                          </div>
                          {existing && (
                            <span className="text-xs text-amber-400">Already imported</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Import progress */}
            {ytImporting && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-surface-600">
                  <Loader2 size={14} className="animate-spin" />
                  Importing... {ytImportProgress}%
                </div>
                <div className="w-full h-2 bg-surface-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-brand-600 rounded-full transition-all duration-300"
                    style={{ width: `${ytImportProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Actions */}
            {ytVideos.length > 0 && !ytImporting && (
              <div className="flex justify-end gap-3 pt-2 border-t border-surface-200">
                <button
                  onClick={() => { setShowYouTubeModal(false); setYtVideos([]); setYoutubeUrl(''); }}
                  className="px-4 py-2 text-sm font-medium text-surface-600 hover:text-surface-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleYouTubeImport}
                  disabled={ytSelected.size === 0}
                  className="flex items-center gap-2 px-5 py-2 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-700 disabled:opacity-50 transition-colors"
                >
                  <Download size={14} />
                  Import {ytSelected.size} as Drafts
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
